import { SelectParticipantComponent } from './../../participant-list/select-participant/select-participant.component';
import { CreateMatchService } from './create-match.service';
import { DisciplineEnum } from './../../_shared/models/csTypes/enums/discipline';
import { Component, Input, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { IonInput, ModalController } from '@ionic/angular';
import { finalize, takeUntil } from 'rxjs/operators';
import { ToastService } from '../../_core/services/toast.service';
import { SelectTeamPlayerComponent } from '../../_shared/components/select-team-player/select-team-player.component';
import { CollectionDefinition, Tie } from '../../_shared/models/tie.model';
import { User } from '../../_shared/models/user.model';
import { AuthService } from '../../auth/auth.service';
import { MatchService } from '../../_core/services/match.service';
import { Match, Schedule } from '../../_shared/models/match.model';
import { Tournament } from '../../_shared/models/tournament.model';
import { TieDetailsService } from '../../ties/tie-details/tie-details.service';
import { format, parseISO, roundToNearestMinutes } from 'date-fns';
import { BoardHelpService } from '../../match-tracker/tracker-board/board-help.service';
import { ClockName } from '../../_shared/models/enums/clock-name.enum';
import { ClockType } from '../../_shared/models/enums/clock-type.enum';
import { ParticipantTypeEnum } from '../../_shared/models/csTypes/enums/participant_type';
import { PolicyService } from '../../_core/services/policy.service';
import { Subject } from 'rxjs';
import { Router } from '@angular/router';

@Component({
  standalone: false,
    selector: 'app-create-match',
    templateUrl: './create-match.component.html',
    styleUrls: ['./create-match.component.scss'],
})

export class CreateMatchComponent implements OnInit, OnDestroy {
    createMatchService = inject(CreateMatchService);
    private bhs = inject(BoardHelpService);
    public authService = inject(AuthService);
    policyService = inject(PolicyService);
    private readonly destroy$: Subject<void> = new Subject();
    public router = inject(Router);


    @ViewChild('content') content: any;
    @Input() tie: Tie;
    @Input() tieId: string; // This is UUID
    @Input() tournament: Tournament;
    @Input() collectionDefinition: CollectionDefinition;
    @Input() collectionPosition: number;
    @Input() match: Match;
    @Input() partner: string;
    @Input() code: string;

    form: UntypedFormGroup;
    side1Form: UntypedFormArray;
    side2Form: UntypedFormArray;
    isEdit = false;

    side1Player: string;
    side2Player: string;
    playerLabel: string;
    matchFormat = 'SET3-S:6/TB7@6';

    formActive = false;
    isLoading = false;
    participantLabel: string;
    policyList: any[] = [];
    selectedPolicy: any;
    isIndividualPalge: boolean = false;
    previousMatchFormat: string;

    constructor(
        private matchService: MatchService,
        private fb: UntypedFormBuilder,
        private modalCtrl: ModalController,
        private toast: ToastService,
        private tieDetailsService: TieDetailsService,
    ) { }

    async ngOnInit() {
        if (this.match) {
            this.isEdit = true;
        }

        if (this.tieId) {
            this.tie = await this.tieDetailsService.getTieInfo(this.tieId, false, false, 'create-match ln 68');
        }

        if (this.partner && this.code) {
            this.isEdit = true;
            this.getMatchFromPartner();
        } else {
            this.buildForm();
        }

        this.getAllLplicies();

        this.isIndividualPalge = this.router.url.includes('individual');
    }

    // TODO Not sure what this is used for???
    private getMatchFromPartner() {
        this.matchService
            .createPartnerMatch(this.partner, this.code)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: res =>{
                    this.match = res.data;
                    this.modalCtrl.dismiss({
                        match: this.match
                    });
                },
                error: () => this.modalCtrl.dismiss()
            });
    }

    private buildForm() {
        let indoorOutdoor = 'OUTDOOR';
        let matchType = 'S';

        this.matchFormat = this.getMatchFormat();

        if (this.match) {
            if (this.match.matchType) matchType = this.match.matchType;
            if (this.match.indoorOutdoor) indoorOutdoor = this.match.indoorOutdoor;
        } else if (this.tie && this.collectionDefinition) {
            matchType = this.collectionDefinition.matchUpType === 'SINGLES' ? 'S' : 'D';
        }

        const surface = this.isEdit
            ? this.match.surfaceCategory || 'HARD'
            : 'HARD';

        let startDate: string;

        if (this.tournament) {
            if (this.match?.schedule?.scheduledDate) {
                startDate = this.match.schedule.scheduledDate;
            } else {
                const date = format(new Date(), 'yyyy-MM-dd');

                if (this.tournament.startDate > date) {
                    const tournamentStartDate = format(new Date(this.tournament.startDate), 'yyyy-MM-dd');
                    startDate = tournamentStartDate;
                } else if (this.tournament.endDate < date) {
                    const tournamentEndDate = format(new Date(this.tournament.endDate), 'yyyy-MM-dd')
                    startDate = tournamentEndDate
                } else {
                    startDate = date
                }
            }
        }

        const startTime = this.match?.schedule?.scheduledTime
            ? this.match.schedule.scheduledTime
            : format(roundToNearestMinutes(new Date(), { nearestTo: 30 }), 'HH:ss');

        const timezone = this.match?.schedule?.timezoneOffset
            ? this.match.schedule.timezoneOffset
            : new Date().getTimezoneOffset();

        this.form = this.fb.group({
            startDate: [new Date(`${startDate}T${startTime}Z`).toJSON()],
            schedule: this.fb.group({
                scheduledDate: [startDate],
                scheduledTime: [startTime],
                timezoneOffset: [timezone]
            }),
            dist: this.fb.group({
                owner: [this.authService.authUserSignal()?._id],
                viewers: [[]]
            }),
            roundName: [],
            indoorOutdoor: [indoorOutdoor],
            matchFormat: [this.matchFormat, Validators.required],
            matchStatus: [this.isEdit ? this.match.matchStatus : 'TBP'],
            matchType: [matchType, Validators.required],
            score: [],
            surfaceCategory: [surface],
            updated: [''],
            winningSide: [this.isEdit ? this.match.winningSide : ''],
            side1Id: [''],
            side2Id: [''],
            side1PartnerId: [''],
            side2PartnerId: [''],
            side1ParticipantId: [''],
            side2ParticipantId: [''],
            tournament: [this.isEdit ? this.match.tournament : this.tournament?._id],
            discipline: [this.getDiscipline()],
            policy: [this.match?.policy],
        });

        this.getParticipantLabel();

        this.form
            .get('discipline')
            .valueChanges
            .subscribe(
                value => {
                    if (value === DisciplineEnum.PICKLEBALL) {
                        this.matchFormat = "SET3-S:1/WB2-G:11/S";
                    } else if (value === DisciplineEnum.BADMINTON) {
                        //this.matchFormat = "SET3-S:1/WB2-G:21/R";
                        this.matchFormat = "SET3-S:TB21@30";
                    } else if (value === DisciplineEnum.TABLE_TENNIS) {
                        this.matchFormat = "SET5-S:TB11";
                    } else {
                        this.matchFormat = "SET3-S:6/TB7@6";
                    }

                    this.updateCollectionMatchFormat(this.matchFormat);
                }
            );
        // Change label when match type is changed
        this.form
            .get('matchType')
            .valueChanges
            .subscribe(
                value => {

                    this.getParticipantLabel();
                    if (this.tournament?.settings) {
                       this.matchFormat = value === 'S' ? this.tournament.settings.singlesMatchUpFormat : this.tournament.settings.doublesMatchUpFormat;
                    }
                }
            );


        this.form
            .get('startDate')
            .valueChanges
            .subscribe(
                value => {
                    this.setSchedule(parseISO(value));
                }
            );

        if (this.isEdit) {
            this.form.get('matchType').setValue(this.match.matchType);

            const playersSide1: any = this.match.sides[0].players.map(p => p.participant);
            this.side1Player = this.getPlayerNames(playersSide1);
            this.setPlayerIdsInForm(playersSide1, 1);

            const playersSide2: any = this.match.sides[1].players.map(p => p.participant);
            this.side2Player = this.getPlayerNames(playersSide2);
            this.setPlayerIdsInForm(playersSide2, 2);
        }

        this.formActive = true;
    }

    private getMatchFormat(): string {
        if (this.match && this.match.matchFormat) {
            return this.match.matchFormat;
        } else if (this.collectionDefinition) {
            return this.collectionDefinition.matchUpFormat;
        } else if (this.tournament?.settings?.singlesMatchUpFormat) {
            return this.tournament?.settings?.singlesMatchUpFormat
        }
    }

    private getDiscipline(): DisciplineEnum {

        if (this.isEdit) {
            return this.match?.discipline!;
        } else {
            return this.tournament?.discipline || DisciplineEnum.TENNIS
        }

    }

    updateCollectionMatchFormat(matchFormat) {
        this.form.get('matchFormat').setValue(matchFormat);
    }

    async onSubmit(match: Match) {
        if (this.form.invalid) {
            this.toast.presentDangerToast('Some required fields are missing');
            return;
        }
        this.isLoading = true;
        this.previousMatchFormat = this.getMatchFormat();
        if (this.isEdit) {
            match._id = this.match._id;
            this.matchService
                .edit(match)
                .pipe(finalize(() => (this.isLoading = false)))
                .subscribe({
                    next:(res) => {
                        
                        if(res?.data) {
                            const {data} = res;
                            let isMatchFormatChanged = this.previousMatchFormat !== data.matchFormat? true: false;
                            this.modalCtrl.dismiss({
                                match: {...data, isMatchFormatChanged} 
                            });
                        }
                       
                    },
                    error:(err) => {
                        console.log('err ==', err);
                    }
                });
        } else {

            match.dist.tracking = {
                rallyLength: false,
                serveLocation: false
            };

            match.collectionId = this.collectionDefinition?.collectionId;
            match.collectionPosition = this.collectionPosition;

            match.clocks = [this.createMatchService.getClockConfig(ClockName.DEFAULT, ClockType.STOPWATCH, 1, 'mm:ss')];
            const scoreFormat = this.bhs.getScoreFormat(match.matchFormat, match?.discipline);

            if (scoreFormat.timedSetDuration) {
                match.clocks.push(this.createMatchService.getClockConfig(ClockName.SET_TIME, ClockType.TIMER, scoreFormat.timedSetDuration * 60 * 1000, 'mm:ss'))
            }

            const trackerId: any = (this.tie?.tracker.person as User)?._id;

            this.matchService
                .create(match, this.selectedPolicy?.configuration, this.tie?._id, trackerId)
                .pipe(finalize(() => (this.isLoading = false)))
                .subscribe({
                    next: (res) => {
                        this.modalCtrl.dismiss({
                            match: res.data,
                        });
                    }
                });
        }
    }

    async addPlayer(sideNumber) {
        if (this.tournament?.features?.includes('PARTICIPANTS')) {
            const participantType = this.form.get('matchType').value === 'S' ? ParticipantTypeEnum.INDIVIDUAL : ParticipantTypeEnum.PAIR;

            const modal = await this.modalCtrl.create({
                component: SelectParticipantComponent,
                componentProps: {
                    participantType,
                    _tournamentId: this.tournament._id
                },
                presentingElement: await this.modalCtrl.getTop()
            });

            await modal.present();
            const { data } = await modal.onDidDismiss();

            if (data) {
                if (sideNumber === 1) {
                    this.side1Player = data.displayName;
                } else {
                    this.side2Player = data.displayName;
                }

                const players = participantType === ParticipantTypeEnum.INDIVIDUAL ? [data.person] : data;

                if (participantType === ParticipantTypeEnum.INDIVIDUAL) {
                    this.form.get(`side${sideNumber}Id`).setValue(data.person);
                    this.form.get(`side${sideNumber}ParticipantId`).setValue(data._id);
                } else {
                    this.form.get(`side${sideNumber}Id`).setValue(data.members[0]);
                    this.form.get(`side${sideNumber}ParticipantId`).setValue(data._id);
                    this.form.get(`side${sideNumber}PartnerId`).setValue(data.members[1]);
                }
            }


        } else {
            if ((sideNumber === 1 && !this.side1Player?.length) || (sideNumber === 2 && !this.side2Player?.length)) {
                const modal = await this.modalCtrl.create({
                    component: SelectTeamPlayerComponent,
                    componentProps: {
                        teamId: this.tie?.sides[sideNumber - 1].team._id,
                        participantType: this.form.get('matchType').value === 'S' ? 'INDIVIDUAL' : 'PAIR'
                    },
                    presentingElement: await this.modalCtrl.getTop()
                });

                await modal.present();
                const { data } = await modal.onDidDismiss();

                if (data) {
                    if (sideNumber === 1) {
                        this.side1Player = this.getPlayerNames(data);
                    } else {
                        this.side2Player = this.getPlayerNames(data);
                    }

                    this.setPlayerIdsInForm(data, sideNumber);
                }
            }
        }
    }

    private getPlayerNames(players: User[]) {
        const playersNames:string[] = [];

        if (players) {
            players.forEach((p:any) => {
                if (p && p?.first_name) {
                    playersNames.push(`${p?.last_name?.toUpperCase()}, ${p?.first_name}`);
                }
            });
        }

        let participant = '';
        playersNames.forEach((p, i) => {
            if (i === 1) participant += '/';
            participant += p;
        });

        return participant;
    }

    close() {
        this.modalCtrl.dismiss();
    }

    // Header for action sheet
    customOptions(header) {
        return { header };
    }

    getParticipantLabel() {
        this.participantLabel = this.form.get('matchType').value === 'S' ? 'Player' : 'Doubles Team';
    }

    setPlayerIdsInForm(players: User[], sideNumber) {
        players.forEach((p, i) => {
            if (p) {
                if (i === 0) {
                    this.form.get(`side${sideNumber}Id`).setValue(p._id);
                } else {
                    this.form.get(`side${sideNumber}PartnerId`).setValue(p._id);
                }
            }
        });
    }

    setSchedule(date: Date) {
        const schedule: Schedule = {
            scheduledDate: format(date, 'yyyy-MM-dd'),
            scheduledTime: format(date, 'HH:mm'),
            timezoneOffset: new Date().getTimezoneOffset()
        }

        this.form.get('schedule').setValue(schedule);
    }

    setRoundName(roundName: string) {
        this.form.get('roundName').setValue(roundName);
    }

    clearParticipant(sideNumber: number) {
        if (sideNumber === 1) {
            this.side1Player = null;
            this.form.get('side1Id').setValue('');
            this.form.get('side1PartnerId').setValue('');
        } else {
            this.side2Player = null;
            this.form.get('side2Id').setValue('');
            this.form.get('side2PartnerId').setValue('');
        }
    }

    getAllLplicies() {
        this.policyService
          .getAllPolicies()
          .pipe(takeUntil(this.destroy$))
          .subscribe((res) => {
             this.policyList = res;
        });
    }

    onPolicySelected(event: any) {
        const selectedPolicyId = event.detail.value;
        this.selectedPolicy = this.policyList.find(p => p._id === selectedPolicyId);
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

}
